import React, {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useDebounceFn } from 'ahooks';
import { cn } from '@rocco/utils/cn';
import { Input as RoccoInput } from '@rocco/components/input';
import { Checkbox as RoccoCheckbox } from '@rocco/components/checkbox';
import type { FilterConfigBarItemSimpleRange } from '@rocco/ui/filter-sort/types/filter-config-bar';
import type { RangeFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/range-filter-state';
import type { FilterBarElementProps } from '@rocco/ui/filter-sort/components/filters-bar/FiltersBarElement';
import { useFilterDropdownContext } from '@rocco/ui/filter-sort/components/filters-bar/FilterDropdown';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import { isRangeFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import {
    formatRangeInputValue,
    useRangeInputDraft,
} from '@rocco/ui/filter-sort/hooks/use-range-input-draft';

const FILTER_DEBOUNCE_MS = 500;

interface DimensionValues {
    min: number;
    max: number;
}

/**
 * Grouped size filter component for numeric dimension ranges.
 * Renders all simpleRange filters in a section as a unified panel with:
 * - Auto-managed checkboxes (derived from values)
 * - Pill-shaped min/max inputs with persistent unit suffixes
 * - Debounced auto-apply (500ms after typing stops), plus Enter key and popover close
 * - Visual error state (red text + ring) for out-of-range values
 *
 * Only the first simpleRange filter in a section renders the full UI;
 * subsequent ones return null to avoid duplicate rendering.
 */
export const FiltersBarSimpleRange = (
    props: FilterBarElementProps<
        FilterConfigBarItemSimpleRange,
        RangeFilterState
    >,
) => {
    const { filterConfigItem } = props;
    const { isOpen, onCloseCallbackRef } = useFilterDropdownContext();
    const { searchFilters } = useFiltersContext();
    const { config, filterStates, updateFilter } = searchFilters;

    // Find the section this filter belongs to
    const sizeSection = useMemo(
        () =>
            config.barFilters.find((section) =>
                section.filters.some(
                    (f) => f.filterKey === filterConfigItem.filterKey,
                ),
            ),
        [config.barFilters, filterConfigItem.filterKey],
    );

    // Get all simpleRange filters in this section, hiding dimensions with 0-0 range (no data)
    const dimensionFilters = useMemo(() => {
        const simpleRangeFilters = (sizeSection?.filters.filter(
            (f) => f.type === 'simpleRange',
        ) ?? []) as FilterConfigBarItemSimpleRange[];
        return simpleRangeFilters.filter((f) => {
            const dimState = filterStates.states[f.filterKey];
            if (!dimState || !isRangeFilterState(dimState)) return true;
            return !(dimState.range.min === 0 && dimState.range.max === 0);
        });
    }, [sizeSection?.filters, filterStates.states]);

    // Only the first dimension renders the full grouped UI
    const isFirstDimension =
        dimensionFilters[0]?.filterKey === filterConfigItem.filterKey;

    // Build local state from authoritative filter state.
    // When value === range (no filter applied, or after X clear), return {0,0} so inputs show placeholders.
    const buildLocalDimensions = useCallback((): Record<
        string,
        DimensionValues
    > => {
        const initial: Record<string, DimensionValues> = {};
        for (const dimFilter of dimensionFilters) {
            const dimState = filterStates.states[dimFilter.filterKey];
            if (dimState && isRangeFilterState(dimState)) {
                const isUnfiltered =
                    dimState.value.min === dimState.range.min &&
                    dimState.value.max === dimState.range.max;
                if (isUnfiltered) {
                    initial[dimFilter.filterKey] = { min: 0, max: 0 };
                } else {
                    const divisor =
                        dimState.rangeDivisor > 0 ? dimState.rangeDivisor : 1;
                    initial[dimFilter.filterKey] = {
                        min: (dimState.value.min ?? 0) / divisor,
                        max: (dimState.value.max ?? 0) / divisor,
                    };
                }
            }
        }
        return initial;
    }, [dimensionFilters, filterStates.states]);

    const [localDimensions, setLocalDimensions] =
        useState<Record<string, DimensionValues>>(buildLocalDimensions);

    // Track whether user has actually modified any values since opening the popover
    const isDirtyRef = useRef(false);

    // Re-sync local dimensions from authoritative filter state when the popover opens.
    // This handles external resets (e.g., pill "X" clear button) that happen while the
    // popover is closed — without this, inputs would show stale previously-typed values.
    const prevIsOpenRef = useRef(false);
    useEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            setLocalDimensions(buildLocalDimensions());
            isDirtyRef.current = false;
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen, buildLocalDimensions]);

    // Dimension is "active" when the user has applied a filter (value differs from range)
    // or has locally modified the values since opening the popover.
    const isDimensionActive = (filterKey: string): boolean => {
        const dimState = filterStates.states[filterKey];
        if (!dimState || !isRangeFilterState(dimState)) return false;

        // Active if a filter is already applied (value differs from aggregation range)
        if (
            dimState.value.min !== dimState.range.min ||
            dimState.value.max !== dimState.range.max
        ) {
            return true;
        }

        // Also active if user has locally typed a non-zero value (not yet applied).
        // Compare against 0 (the "no input" sentinel), not range boundaries,
        // to prevent cross-contamination when isDirtyRef is shared across dimensions.
        if (isDirtyRef.current) {
            const vals = localDimensions[filterKey];
            if (!vals) return false;
            return vals.min !== 0 || vals.max !== 0;
        }

        return false;
    };

    // Compute error state for each dimension (derived, not stored).
    // Out-of-range values are intentionally passed to search service — they may
    // return zero results, but the visual error state warns the user.
    const dimensionErrors = useMemo(() => {
        const errors: Record<string, { minError: boolean; maxError: boolean }> =
            {};
        for (const dimFilter of dimensionFilters) {
            const dimState = filterStates.states[dimFilter.filterKey];
            if (!dimState || !isRangeFilterState(dimState)) {
                errors[dimFilter.filterKey] = {
                    minError: false,
                    maxError: false,
                };
                continue;
            }
            const divisor =
                dimState.rangeDivisor > 0 ? dimState.rangeDivisor : 1;
            const vals = localDimensions[dimFilter.filterKey] ?? {
                min: 0,
                max: 0,
            };
            const rangeMax = dimState.range.max / divisor;
            // effectiveMax: what the user intends as the upper bound
            // If user typed a max → use that; otherwise fall back to aggregation max
            const effectiveMax = vals.max > 0 ? vals.max : rangeMax;
            errors[dimFilter.filterKey] = {
                minError: vals.min > 0 && vals.min > effectiveMax,
                maxError: vals.max !== 0 && vals.max > rangeMax,
            };
        }
        return errors;
    }, [dimensionFilters, filterStates.states, localDimensions]);

    // Apply all dimension values to the filter state (no clamping — out-of-range values pass through)
    const applyAllChanges = useCallback(() => {
        // Don't fire request when min exceeds max — would return 0 results
        const hasMinError = Object.values(dimensionErrors).some(
            (e) => e.minError,
        );
        if (hasMinError) return;

        const updatedStates = { ...filterStates.states };
        const clampedDimensions: Record<string, DimensionValues> = {};

        for (const dimFilter of dimensionFilters) {
            const dimState = filterStates.states[dimFilter.filterKey];
            if (!dimState || !isRangeFilterState(dimState)) continue;

            const values = localDimensions[dimFilter.filterKey];
            if (!values) continue;

            const divisor =
                dimState.rangeDivisor > 0 ? dimState.rangeDivisor : 1;
            const internalMin = Math.round(values.min * divisor);
            const internalMax = Math.round(values.max * divisor);

            // 0 means "no limit" — use the full range boundary
            const effectiveMin =
                internalMin === 0 ? dimState.range.min : internalMin;
            const effectiveMax =
                internalMax === 0 ? dimState.range.max : internalMax;

            // Clamp to allowed range to prevent search service errors, then normalise min/max order
            const clampedMin = Math.max(
                0,
                Math.min(effectiveMin, dimState.range.max),
            );
            const clampedMax = Math.max(
                0,
                Math.min(effectiveMax, dimState.range.max),
            );

            updatedStates[dimFilter.filterKey] = {
                ...dimState,
                value: {
                    min: Math.min(clampedMin, clampedMax),
                    max: Math.max(clampedMin, clampedMax),
                },
            };

            // Update local display to reflect clamped values
            const clampedDisplayMin = clampedMin / divisor;
            const clampedDisplayMax = clampedMax / divisor;
            if (
                values.min !== clampedDisplayMin ||
                values.max !== clampedDisplayMax
            ) {
                clampedDimensions[dimFilter.filterKey] = {
                    min:
                        clampedDisplayMin === dimState.range.min / divisor
                            ? 0
                            : clampedDisplayMin,
                    max:
                        clampedDisplayMax === dimState.range.max / divisor
                            ? 0
                            : clampedDisplayMax,
                };
            }
        }

        // Update local dimensions to reflect clamped values
        if (Object.keys(clampedDimensions).length > 0) {
            setLocalDimensions((prev) => ({ ...prev, ...clampedDimensions }));
        }

        updateFilter({ ...filterStates, states: updatedStates }, true);
    }, [
        dimensionErrors,
        dimensionFilters,
        filterStates,
        localDimensions,
        updateFilter,
    ]);

    // Keep a ref to the latest applyAllChanges so the close callback always uses fresh state
    const applyAllChangesRef = useRef(applyAllChanges);
    useEffect(() => {
        applyAllChangesRef.current = applyAllChanges;
    });

    // Debounced auto-apply: fires 500ms after user stops typing
    const { run: scheduleApply, cancel: cancelPendingApply } = useDebounceFn(
        () => {
            applyAllChangesRef.current();
        },
        { wait: FILTER_DEBOUNCE_MS },
    );

    // Detect external resets (e.g., pill X clear) while popover is open.
    // When filterStates.states changes (reference comparison) and all dimensions
    // become "unfiltered" (value === range), re-sync local state so inputs
    // reflect the cleared state.
    const prevStatesRef = useRef(filterStates.states);
    useEffect(() => {
        const prevStates = prevStatesRef.current;
        prevStatesRef.current = filterStates.states;

        // Only act when filterStates.states reference actually changed
        // AND the popover is currently open.
        if (!isOpen || prevStates === filterStates.states) return;

        // Check if all dimensions are now "unfiltered" (value === range)
        const allUnfiltered = dimensionFilters.every((dimFilter) => {
            const s = filterStates.states[dimFilter.filterKey];
            if (!s || !isRangeFilterState(s)) return true;
            return s.value.min === s.range.min && s.value.max === s.range.max;
        });

        if (allUnfiltered) {
            cancelPendingApply();
            setLocalDimensions(buildLocalDimensions());
            isDirtyRef.current = false;
        }
    }, [
        isOpen,
        filterStates.states,
        dimensionFilters,
        buildLocalDimensions,
        cancelPendingApply,
    ]);

    const updateDimensionValue = (
        filterKey: string,
        field: 'min' | 'max',
        value: number,
    ) => {
        isDirtyRef.current = true;
        setLocalDimensions((prev) => ({
            ...prev,
            [filterKey]: {
                min: prev[filterKey]?.min ?? 0,
                max: prev[filterKey]?.max ?? 0,
                [field]: value,
            },
        }));
        scheduleApply();
    };

    // Clear dimension by applying range boundary values directly to avoid stale async state.
    // setLocalDimensions is async, so calling applyAllChanges immediately after would read
    // stale localDimensions. Instead, we update filter state directly with the "no filter" values.
    const clearDimension = (filterKey: string) => {
        cancelPendingApply();
        isDirtyRef.current = true;
        setLocalDimensions((prev) => ({
            ...prev,
            [filterKey]: { min: 0, max: 0 },
        }));

        // Apply directly with range boundary values (the "no filter" state)
        const dimState = filterStates.states[filterKey];
        if (dimState && isRangeFilterState(dimState)) {
            const updatedStates = {
                ...filterStates.states,
                [filterKey]: {
                    ...dimState,
                    value: {
                        min: dimState.range.min,
                        max: dimState.range.max,
                    },
                },
            };
            updateFilter({ ...filterStates, states: updatedStates }, true);
        }
    };

    // Enter key: cancel pending debounce, apply immediately via ref to avoid stale closure
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            cancelPendingApply();
            applyAllChangesRef.current();
        }
    };

    // Register callback for when popover closes — cancel pending debounce and apply.
    // Only the first dimension registers, since non-first instances return null and
    // never receive user input (their isDirtyRef stays false, breaking apply-on-close).
    useEffect(() => {
        if (!isFirstDimension) return;
        onCloseCallbackRef.current = () => {
            cancelPendingApply();
            if (isDirtyRef.current) {
                applyAllChangesRef.current();
            }
        };
        return () => {
            onCloseCallbackRef.current = null;
            cancelPendingApply();
        };
    }, [onCloseCallbackRef, isFirstDimension, cancelPendingApply]);

    // Non-first dimensions return null (the first renders all)
    if (!isFirstDimension) {
        return null;
    }

    return (
        <div className="flex flex-col gap-2">
            {dimensionFilters.map((dimFilter) => {
                const dimState = filterStates.states[dimFilter.filterKey];
                const divisor =
                    dimState &&
                    isRangeFilterState(dimState) &&
                    dimState.rangeDivisor > 0
                        ? dimState.rangeDivisor
                        : 1;
                const rangeMax =
                    dimState && isRangeFilterState(dimState)
                        ? dimState.range.max / divisor
                        : 0;

                return (
                    <DimensionRow
                        key={dimFilter.filterKey}
                        dimFilter={dimFilter}
                        values={
                            localDimensions[dimFilter.filterKey] ?? {
                                min: 0,
                                max: 0,
                            }
                        }
                        rangeMax={rangeMax}
                        isActive={isDimensionActive(dimFilter.filterKey)}
                        minError={
                            dimensionErrors[dimFilter.filterKey]?.minError ??
                            false
                        }
                        maxError={
                            dimensionErrors[dimFilter.filterKey]?.maxError ??
                            false
                        }
                        onUpdateValue={(field, value) =>
                            updateDimensionValue(
                                dimFilter.filterKey,
                                field,
                                value,
                            )
                        }
                        onClear={() => clearDimension(dimFilter.filterKey)}
                        onKeyDown={handleKeyDown}
                    />
                );
            })}
        </div>
    );
};

interface DimensionRowProps {
    dimFilter: FilterConfigBarItemSimpleRange;
    values: DimensionValues;
    rangeMax: number;
    isActive: boolean;
    minError: boolean;
    maxError: boolean;
    onUpdateValue: (field: 'min' | 'max', value: number) => void;
    onClear: () => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * A single dimension row: checkbox + label + min pill + "-" + max pill.
 * Unit suffix (e.g. "mm", "kg") is shown persistently via RoccoInput's postfix prop.
 */
const DimensionRow = ({
    dimFilter,
    values,
    rangeMax,
    isActive,
    minError,
    maxError,
    onUpdateValue,
    onClear,
    onKeyDown,
}: DimensionRowProps) => {
    const labelId = useId();
    const decimalPlaces = dimFilter.decimalPlaces ?? 0;
    const { readInput, getInputText, clearDraft } =
        useRangeInputDraft(decimalPlaces);
    const inputMode = decimalPlaces > 0 ? 'decimal' : 'numeric';

    const handleNumericChange =
        (field: 'min' | 'max') => (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = readInput(field, e.currentTarget.value);
            onUpdateValue(field, Math.max(0, value));
        };

    const unitPostfix = dimFilter.unit ? (
        <span className="text-xs text-gray-400">{dimFilter.unit}</span>
    ) : undefined;

    return (
        <div
            role="group"
            aria-labelledby={labelId}
            className="flex items-center gap-2 px-2"
        >
            {/* Auto-managed checkbox: checked when values > 0, uncheck clears values */}
            <RoccoCheckbox
                checked={isActive}
                onCheckedChange={(checked) => {
                    if (!checked) onClear();
                }}
                className="size-[1.125rem] shrink-0"
                aria-label={`Enable ${dimFilter.label} filter`}
            />

            {/* Label with colon */}
            <span
                id={labelId}
                className="w-16.75 shrink-0 text-sm text-gray-900"
            >
                {dimFilter.label}:
            </span>

            {/* Min/Max inputs with dash separator */}
            <div className="flex items-center gap-2.25">
                {/* Min input */}
                <RoccoInput
                    type="text"
                    inputMode={inputMode}
                    placeholder={dimFilter.minPlaceholder}
                    postfix={unitPostfix}
                    color="transparent"
                    outline="none"
                    focus="none"
                    shape="rounded"
                    containerClassName="w-[7rem] shrink-0"
                    className={cn(
                        'h-[2.625rem] text-left font-medium tracking-tight placeholder:font-normal',
                        minError ? 'text-red-500 ring-1 ring-red-500' : '',
                        values.min > 0 ? 'bg-white' : 'bg-[#f3f3f3]',
                    )}
                    value={getInputText(
                        'min',
                        values.min
                            ? formatRangeInputValue(values.min, decimalPlaces)
                            : '',
                    )}
                    onChange={handleNumericChange('min')}
                    onBlur={clearDraft}
                    onKeyDown={onKeyDown}
                    aria-label={`Minimum ${dimFilter.label}`}
                    aria-invalid={minError}
                />

                {/* Dash separator */}
                <span className="shrink-0 text-base opacity-40">-</span>

                {/* Max input — show user-typed value, or fall back to aggregation max */}
                <RoccoInput
                    type="text"
                    inputMode={inputMode}
                    placeholder={dimFilter.maxPlaceholder}
                    postfix={unitPostfix}
                    color="transparent"
                    outline="none"
                    focus="none"
                    shape="rounded"
                    containerClassName="w-[7rem] shrink-0"
                    className={cn(
                        'h-[2.625rem] text-left font-medium tracking-tight placeholder:font-normal',
                        maxError ? 'text-red-500 ring-1 ring-red-500' : '',
                        values.max > 0 ? 'bg-white' : 'bg-[#f3f3f3]',
                    )}
                    value={getInputText(
                        'max',
                        values.max
                            ? formatRangeInputValue(values.max, decimalPlaces)
                            : rangeMax
                              ? formatRangeInputValue(rangeMax, decimalPlaces)
                              : '',
                    )}
                    onChange={handleNumericChange('max')}
                    onBlur={clearDraft}
                    onKeyDown={onKeyDown}
                    aria-label={`Maximum ${dimFilter.label}`}
                    aria-invalid={maxError}
                />
            </div>
        </div>
    );
};
